import * as THREE from 'three';
import { type FC, useCallback, useMemo, useRef, useState } from 'react';
import { type ThreeEvent, useFrame, useThree } from '@react-three/fiber';
import { DraggingHelper, type DraggingInfo } from './DraggingHelper';
import { ViewerLayer } from './ViewerLayer';
import { CALLBACK_ORDER_AFTER_CAMERA_UPDATE } from './FrameCallbackOrder';
import type { Number3 } from '../types';
import { RENDER_ORDER_TRANSFORM_CONTROLS } from './RenderOrder';

export interface DraggableCubeProps {
  draggingHelper: DraggingHelper;
  position: Number3;
  onStartDragging: () => void;
  onDragging: (info: DraggingInfo) => void;
  onStopDragging: () => void;
  draggingDirection: Number3;
  draggingStep: Number3;
  size: number;
  color: number;
  draggingColor?: number;
  getLocalGroup: () => THREE.Group | undefined;
}

export const DraggableCube: FC<DraggableCubeProps> = ({
  draggingHelper,
  position,
  onStartDragging: onStartDraggingOnParent,
  onDragging: onDraggingOnParent,
  onStopDragging: onStopDraggingOnParent,
  draggingDirection,
  draggingStep,
  size,
  color,
  draggingColor,
  getLocalGroup,
}) => {
  const camera = useThree((state) => state.camera);

  const onDragging = useCallback(
    (event: PointerEvent, info: DraggingInfo) => {
      onDraggingOnParent(info);
    },
    [onDraggingOnParent]
  );

  const mesh = useRef<THREE.Mesh | null>(null);

  const [isDragging, setIsDragging] = useState(false);

  const onStopDragging = useCallback(() => {
    onStopDraggingOnParent();
    setIsDragging(false);
  }, [onStopDraggingOnParent]);

  const startDragging = (event: ThreeEvent<PointerEvent>) => {
    const localGroup = getLocalGroup();
    if (!localGroup) return;

    onStartDraggingOnParent();
    setIsDragging(true);

    draggingHelper.startDragging({
      type: 'line',
      event: event.nativeEvent,
      onDragging,
      onStop: onStopDragging,
      startPosition: position,
      draggingDirection,
      draggingStep,
      localGroup,
    });
    event.stopPropagation();
  };

  const layers = useMemo(() => {
    const layers = new THREE.Layers();
    layers.enable(ViewerLayer.AutoPickable);
    return layers;
  }, []);

  const tmp = useRef({
    cameraPosition: new THREE.Vector3(),
    meshPosition: new THREE.Vector3(),
  });

  useFrame(() => {
    if (!mesh.current) return;
    let scale = size;
    if (camera instanceof THREE.OrthographicCamera) {
      scale = size / camera.zoom;
    } else if (camera instanceof THREE.PerspectiveCamera) {
      const { cameraPosition, meshPosition } = tmp.current;
      camera.getWorldPosition(cameraPosition);
      mesh.current.getWorldPosition(meshPosition);

      const fovRadians = (camera.fov * Math.PI) / 180;
      const angularSize = (size / 110) * fovRadians;

      const distance = cameraPosition.distanceTo(meshPosition);

      const slope = Math.tan(angularSize / 2);
      scale = size * slope * distance;
    }
    mesh.current.scale.set(scale, scale, scale);
  }, CALLBACK_ORDER_AFTER_CAMERA_UPDATE);

  const currentColor = isDragging ? (draggingColor ?? color) : color;
  return (
    <>
      <mesh
        ref={mesh}
        layers={layers}
        position={position}
        scale={1}
        onPointerDown={startDragging}
        renderOrder={RENDER_ORDER_TRANSFORM_CONTROLS}
      >
        <boxGeometry attach="geometry" />
        <meshBasicMaterial
          color={currentColor}
          depthTest={false}
          transparent={true}
          attach="material"
        />
      </mesh>
    </>
  );
};
